import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { AuthenticationFacade } from '@planone/bau365/data-access-authentication';
import * as DataAccessFairData from '@planone/bau365/data-access-fair-data';
import { DataAccessExhibitorsFacade, DataAccessFairsFacade } from '@planone/bau365/data-access-fair-data';
import { ContactPerson, Exhibitor } from '@planone/bau365/util-fair-data';
import * as FeatureTrackingGoogleAnalytics from '@planone/shared/feature-tracking-google-analytics';
import * as FeatureTrackingSnowplow from '@planone/shared/feature-tracking-snowplow';
import { localizedMeta, MetaService } from '@planone/shared/util-meta';
import { delay, filter, map, of, switchMap, take, withLatestFrom } from 'rxjs';

import { DataTransformationHelper } from '../../helper/data-transformation.helpers';
import { CustomEventIglu } from '../../tracking-types/snowplow/schema/schema.interface';

@Injectable()
export class ExhibitorsEffects {
  pageViewDirectory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessFairData.exhibitorDirectoryOpenedAnywhere),
      withLatestFrom(this.authenticationFacade.excludeAnalytics$, this.fairsFacade.isBauInsights$),
      map(
        ([action, exclude_analytics, insights]) =>
          <[{ fairId: string } & Action<'[Exhibitor Page] Directory opened anywhere'>, boolean, boolean]>[
            action,
            exclude_analytics,
            insights,
          ]
      ),
      delay(0),
      switchMap(([action, exclude_analytics, insights]) => {
        let title = '';

        if (insights) {
          title = this.metaService.getTitle(localizedMeta['titleCompanyDirectory']);
        } else {
          title = this.metaService.getTitle(localizedMeta['titleExhibitorDirectory']);
        }

        return of(
          FeatureTrackingSnowplow.pageView({
            title: title,
            context: [
              {
                schema: CustomEventIglu.page_view,
                data: {
                  context: action.fairId,
                  page_type: 'exhibitors',
                  exclude_analytics: exclude_analytics,
                },
              },
            ],
          }),
          FeatureTrackingGoogleAnalytics.push({
            event: 'pageview',
            context: action.fairId,
            page_type: 'exhibitors',
            exclude_analytics: exclude_analytics,
          })
        );
      })
    )
  );

  pageViewDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessFairData.exhibitorOpened),
      switchMap((action) =>
        this.exhibitorsFacade.exhibitor$.pipe(
          filter((exhibitor) => !!exhibitor && exhibitor.id === action.exhibitorId),
          take(1)
        )
      ),
      withLatestFrom(this.authenticationFacade.excludeAnalytics$),
      map(([exhibitor, exclude_analytics]) => <[Exhibitor, boolean]>[exhibitor, exclude_analytics]),
      delay(0),
      switchMap(([exhibitor, exclude_analytics]) => {
        const title = this.metaService.getTitle(localizedMeta['titleExhibitorDetails'], [exhibitor.name ?? '']);

        const snowplowExhibitorContext = this.dataTransformationHelper.snowplow?.map('EXHIBITOR', exhibitor);

        const snowplowContext = [
          {
            schema: CustomEventIglu.page_view,
            data: {
              context: exhibitor.fair_id,
              page_type: 'exhibitor-details',
              exclude_analytics: exclude_analytics,
            },
          },
        ];

        if (snowplowExhibitorContext) snowplowContext.push(snowplowExhibitorContext);

        return of(
          FeatureTrackingSnowplow.pageView({
            title: title,
            context: snowplowContext ? snowplowContext : undefined,
          }),
          FeatureTrackingGoogleAnalytics.push({
            event: 'pageview',
            context: exhibitor.fair_id,
            page_type: 'exhibitor-details',
            exclude_analytics: exclude_analytics,
            ...this.dataTransformationHelper.googleAnalytics?.map('EXHIBITOR', exhibitor),
          })
        );
      })
    )
  );

  pageViewContactPersonDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessFairData.exhibitorContactPersonOpened),
      switchMap((action) =>
        this.exhibitorsFacade.exhibitor$.pipe(
          filter((exhibitor) => !!exhibitor && exhibitor.id === action.exhibitorId),
          take(1)
        )
      ),
      switchMap((exhibitor) =>
        this.exhibitorsFacade.exhibitorContactPerson$.pipe(
          filter((contactPerson) => !!contactPerson),
          take(1),
          withLatestFrom(this.authenticationFacade.excludeAnalytics$),
          map(
            ([contactPerson, exclude_analytics]) =>
              <[Exhibitor, ContactPerson, boolean]>[exhibitor, contactPerson, exclude_analytics]
          ),
          delay(0),
          switchMap(([exhibitor, contactPerson, exclude_analytics]) => {
            const title = this.metaService.getTitle(localizedMeta['titleExhibitorContactPersonDetails'], [
              'Anonymized',
            ]);

            const snowplowExhibitorContext = this.dataTransformationHelper.snowplow?.map('EXHIBITOR', exhibitor);
            const snowplowContactPersonContext = this.dataTransformationHelper.snowplow?.map(
              'CONTACT_PERSON',
              contactPerson,
              exhibitor.fair_id
            );

            const snowplowContext = [
              {
                schema: CustomEventIglu.page_view,
                data: {
                  context: exhibitor.fair_id,
                  page_type: 'contact-person',
                  exclude_analytics: exclude_analytics,
                },
              },
            ];

            if (snowplowExhibitorContext) snowplowContext.push(snowplowExhibitorContext);
            if (snowplowContactPersonContext) snowplowContext.push(snowplowContactPersonContext);

            return of(
              FeatureTrackingSnowplow.pageView({
                title: title,
                context: snowplowContext ? snowplowContext : undefined,
              }),
              FeatureTrackingGoogleAnalytics.push({
                event: 'pageview',
                context: exhibitor.fair_id,
                page_type: 'contact-person',
                exclude_analytics: exclude_analytics,
                page_title: title,
                ...this.dataTransformationHelper.googleAnalytics?.map('EXHIBITOR', exhibitor),
                ...this.dataTransformationHelper.googleAnalytics?.map('CONTACT_PERSON', contactPerson),
              })
            );
          })
        )
      )
    )
  );

  constructor(
    private readonly actions$: Actions,
    private store: Store,
    private authenticationFacade: AuthenticationFacade,
    private exhibitorsFacade: DataAccessExhibitorsFacade,
    private fairsFacade: DataAccessFairsFacade,
    private metaService: MetaService,
    private dataTransformationHelper: DataTransformationHelper
  ) {}
}
